import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RiEditLine,
  RiDeleteBinLine,
  RiAddLine,
  RiCheckLine,
  RiCloseLine,
  RiLoader4Line,
  RiAlertLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { formatPrice } from '@/utils';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import {
  patchBracketsCache,
  patchOrgBracketsInAdminCache,
  patchOrganizationBracketsCache,
} from '../utils';

// Coerce the BE's string amounts to numbers on read.
const num = (v) => {
  if (v == null || v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

// max_amount = 0 (or null) is the BE's "unbounded above" sentinel.
const isUnbounded = (raw) => raw == null || Number(raw) === 0;

/**
 * Warn if two brackets overlap on the [min, max) half-open interval.
 * Client-side hint only — BE remains the source of truth.
 * @returns {string|null} warning text, or null if clean
 */
const overlapWarning = (draft, others) => {
  const dMin = num(draft.min_amount);
  const dMaxRaw = draft.max_amount;
  const dMax = isUnbounded(dMaxRaw) ? Infinity : num(dMaxRaw);
  if (!Number.isFinite(dMin) || !Number.isFinite(dMax) && dMax !== Infinity) return null;
  for (const o of others) {
    const oMin = num(o.min_amount);
    const oMax = isUnbounded(o.max_amount) ? Infinity : num(o.max_amount);
    if (!Number.isFinite(oMin)) continue;
    // Half-open overlap: !(dMax <= oMin || oMax <= dMin)
    const disjoint = dMax <= oMin || oMax <= dMin;
    if (!disjoint) {
      const oLabel = `${formatPrice(oMin)} – ${oMax === Infinity ? 'unbounded' : formatPrice(oMax)}`;
      return `Overlaps existing bracket ${oLabel}.`;
    }
  }
  return null;
};

const BracketRowForm = ({
  initial,
  otherBrackets,
  disallowUnbounded,
  onCancel,
  onSubmit,
  submitting,
}) => {
  const [minAmount, setMinAmount] = useState(
    initial?.min_amount != null ? String(num(initial.min_amount)) : '0',
  );
  const [maxAmount, setMaxAmount] = useState(
    initial?.max_amount != null && !isUnbounded(initial.max_amount)
      ? String(num(initial.max_amount))
      : '',
  );
  const [unbounded, setUnbounded] = useState(() => {
    // Explicit override for the create form's smart default. Falls
    // back to detecting an unbounded max on the edit `initial`.
    if (initial && typeof initial.unbounded === 'boolean') return initial.unbounded;
    return !!initial && isUnbounded(initial.max_amount);
  });
  const [feePercent, setFeePercent] = useState(
    initial?.fee_percent != null ? String(num(initial.fee_percent)) : '',
  );

  const parsedMin = num(minAmount);
  const parsedMax = unbounded ? 0 : num(maxAmount);
  const parsedFee = num(feePercent);

  const errors = [];
  if (!Number.isFinite(parsedMin) || parsedMin < 0) errors.push('Min must be a non-negative number.');
  if (!unbounded) {
    if (!Number.isFinite(parsedMax) || parsedMax <= 0) errors.push('Max must be a positive number, or mark it unbounded.');
    else if (parsedMax <= parsedMin) errors.push('Max must be greater than Min.');
  }
  if (!Number.isFinite(parsedFee) || parsedFee <= 0) errors.push('Fee % must be greater than zero.');

  // Overlap is now a HARD error, not a warning. The resolver uses a
  // first-match on the min-sorted list, so any two overlapping
  // brackets leave one of them dead code (whichever sorts later
  // never gets picked). Block save until the ranges are disjoint.
  const overlapError = errors.length === 0
    ? overlapWarning({ min_amount: parsedMin, max_amount: unbounded ? 0 : parsedMax }, otherBrackets)
    : null;
  const allErrors = overlapError ? [...errors, overlapError] : errors;

  const canSubmit = allErrors.length === 0 && !submitting;

  return (
    <tr className="border-t border-slate-100 bg-primary-50/30">
      <td className="px-3 py-2 align-top">
        <Input
          type="number"
          min="0"
          step="1"
          value={minAmount}
          onChange={(e) => setMinAmount(e.target.value)}
          disabled={submitting}
          className="h-9 w-full bg-white !border !border-slate-200 rounded-md text-sm px-2"
          placeholder="0"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1">
          <Input
            type="number"
            min="0"
            step="1"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            disabled={submitting || unbounded}
            className={`h-9 w-full bg-white !border !border-slate-200 rounded-md text-sm px-2 ${unbounded ? 'opacity-40' : ''}`}
            placeholder="50000"
          />
          <label className={`inline-flex items-center gap-1.5 text-[11px] text-slate-600 select-none ${disallowUnbounded && !unbounded ? 'opacity-40 pointer-events-none' : ''}`}>
            <input
              type="checkbox"
              checked={unbounded}
              onChange={(e) => setUnbounded(e.target.checked)}
              disabled={submitting || (disallowUnbounded && !unbounded)}
              className="h-3.5 w-3.5 rounded border-slate-300"
            />
            Unbounded
            {disallowUnbounded && !unbounded && (
              <span className="text-[10px] text-slate-400">(one exists)</span>
            )}
          </label>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="relative">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={feePercent}
            onChange={(e) => setFeePercent(e.target.value)}
            disabled={submitting}
            className="h-9 w-full bg-white !border !border-slate-200 rounded-md text-sm px-2 pr-6"
            placeholder="1.5"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-50"
            aria-label="Cancel"
          >
            <RiCloseLine className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => canSubmit && onSubmit({
              min_amount: parsedMin,
              // Send `null` for the "unbounded above" case. The BE
              // schema on gtj-dev appears to reject `max_amount = 0`
              // (likely a `CHECK max_amount > min_amount` or similar),
              // so we use SQL NULL as the sentinel instead. The read
              // side (both here and in utils/platformFee.js) already
              // treats null and 0 identically.
              max_amount: unbounded ? null : parsedMax,
              fee_percent: parsedFee,
            })}
            disabled={!canSubmit}
            className="p-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Save"
          >
            {submitting ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiCheckLine className="w-4 h-4" />}
          </button>
        </div>
        {allErrors.length > 0 && (
          <div className="mt-1 text-[11px] text-right space-y-0.5">
            {allErrors.map((e) => (
              <p key={e} className="text-rose-600">{e}</p>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
};

const BracketRow = ({ bracket, onEdit, onDelete, deleting }) => {
  const min = num(bracket.min_amount);
  const rawMax = bracket.max_amount;
  const unbounded = isUnbounded(rawMax);
  const feePercent = num(bracket.fee_percent);
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2 text-slate-700">
        {Number.isFinite(min) ? formatPrice(min) : '—'}
      </td>
      <td className="px-4 py-2 text-slate-700">
        {unbounded ? (
          <span className="text-slate-500 italic">Unbounded</span>
        ) : (
          formatPrice(num(rawMax))
        )}
      </td>
      <td className="px-4 py-2 text-slate-700 font-medium">
        {Number.isFinite(feePercent) ? `${feePercent}%` : '—'}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Edit bracket"
          >
            <RiEditLine className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="p-1.5 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
            aria-label="Delete bracket"
          >
            {deleting ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiDeleteBinLine className="w-4 h-4" />}
          </button>
        </div>
      </td>
    </tr>
  );
};

const FeesTab = ({ organization, open }) => {
  const queryClient = useQueryClient();
  const orgId = organization?.organization_id;

  const {
    data: brackets = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['org-fee-brackets', orgId],
    queryFn: () => apiClient.listPlatformFeeBrackets(orgId),
    enabled: !!orgId && open,
    staleTime: 30 * 1000,
  });

  const [editingId, setEditingId] = useState(null); // bracket id under edit
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null); // row currently deleting

  const sorted = useMemo(
    () => [...brackets].sort((a, b) => num(a.min_amount) - num(b.min_amount)),
    [brackets],
  );

  // Every mutation writes directly to three caches so the tab +
  // orgs-admin table + every usePlatformFeeRate consumer (Edit
  // Pricing drawer, Dashboard, Financials) all see fresh brackets
  // immediately — no network round-trip, no 5-minute staleTime wait.
  //
  // Without patching ['organization', orgId], broker-side pricing
  // kept falling back to organization.platform_fee_percent (often
  // 10% in test fixtures) until the shared org cache refreshed.
  const applyBracketPatch = (updater) => {
    patchBracketsCache(queryClient, orgId, updater);
    patchOrgBracketsInAdminCache(queryClient, orgId, updater);
    patchOrganizationBracketsCache(queryClient, orgId, updater);
  };

  const bracketIdOf = (b) => b?.organization_platform_fee_bracket_id;

  const createMutation = useMutation({
    mutationFn: (payload) => apiClient.createPlatformFeeBracket(orgId, payload),
    onSuccess: (created) => {
      toast.success('Bracket added.', { position: 'bottom-right' });
      setCreating(false);
      // Server echoes the new row (with the real id + timestamps);
      // fall back to a best-effort local object if the BE returns
      // nothing so the UI still updates.
      applyBracketPatch((prev) => [...prev, created || {}]);
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to add bracket.', { position: 'bottom-right' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ bracketId, patch }) =>
      apiClient.updatePlatformFeeBracket(orgId, bracketId, patch),
    onSuccess: (updated, { bracketId, patch }) => {
      toast.success('Bracket updated.', { position: 'bottom-right' });
      setEditingId(null);
      applyBracketPatch((prev) => prev.map((b) => (
        bracketIdOf(b) === bracketId
          ? { ...b, ...(updated || patch) }
          : b
      )));
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to update bracket.', { position: 'bottom-right' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (bracketId) => apiClient.deletePlatformFeeBracket(orgId, bracketId),
    onSuccess: (_res, bracketId) => {
      toast.success('Bracket deleted.', { position: 'bottom-right' });
      setBusyId(null);
      applyBracketPatch((prev) => prev.filter((b) => bracketIdOf(b) !== bracketId));
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to delete bracket.', { position: 'bottom-right' });
      setBusyId(null);
    },
  });

  // Block adding a second unbounded row — only one open-top bracket
  // is meaningful (the resolver picks whichever the sort produces).
  const hasUnbounded = sorted.some((b) => isUnbounded(b.max_amount));
  const disallowUnboundedForNew = hasUnbounded;
  const disallowUnboundedForRow = (editingBracket) =>
    hasUnbounded && !isUnbounded(editingBracket?.max_amount);

  // Smart prefill for a fresh row: min = the top of the tallest
  // finite bracket, unbounded ON when no other unbounded exists.
  // Snaps a new tier to the "next slot up" so non-overlap is the
  // natural default. Falls back to (0, unbounded) on an empty list.
  const highestFiniteMax = sorted.reduce((max, b) => {
    if (isUnbounded(b.max_amount)) return max;
    const m = num(b.max_amount);
    return Number.isFinite(m) && m > max ? m : max;
  }, 0);
  const createInitial = {
    min_amount: highestFiniteMax,
    unbounded: !hasUnbounded,
  };

  // Post-save gap detection — surfaces any dollar bands that fall
  // through to `organization.platform_fee_percent` (or the 1.5%
  // default). Non-blocking — sometimes gaps are intentional.
  const gaps = [];
  if (sorted.length > 0) {
    if (num(sorted[0].min_amount) > 0) {
      gaps.push({ from: 0, to: num(sorted[0].min_amount) });
    }
    for (let i = 0; i < sorted.length - 1; i += 1) {
      const curr = sorted[i];
      const next = sorted[i + 1];
      if (isUnbounded(curr.max_amount)) break;
      const currMax = num(curr.max_amount);
      const nextMin = num(next.min_amount);
      if (Number.isFinite(currMax) && Number.isFinite(nextMin) && currMax < nextMin) {
        gaps.push({ from: currMax, to: nextMin });
      }
    }
    const top = sorted[sorted.length - 1];
    if (top && !isUnbounded(top.max_amount)) {
      const topMax = num(top.max_amount);
      if (Number.isFinite(topMax)) gaps.push({ from: topMax, to: Infinity });
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
        <RiLoader4Line className="w-4 h-4 animate-spin text-primary-500" />
        Loading brackets…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">
        Failed to load brackets ({error.message || 'unknown error'}).
      </div>
    );
  }

  const isEmpty = sorted.length === 0 && !creating;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
          Platform fee brackets
        </p>
        {!creating && (
          <Button
            type="button"
            variant="secondary"
            mode="stroke"
            className="!font-normal !border-slate-200 hover:!bg-slate-50 rounded-full text-sm"
            onClick={() => setCreating(true)}
          >
            <span className="inline-flex items-center gap-1.5">
              <RiAddLine className="w-4 h-4" />
              Add tier
            </span>
          </Button>
        )}
      </div>

      {isEmpty ? (
        <div className="p-6 text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-xl">
          No platform fee brackets configured yet. This org will fall back
          to <span className="font-mono">organization.platform_fee_percent</span>{' '}
          (or 1.5% default) until you add tiers.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                <th className="text-left px-4 py-2 w-1/4">Min</th>
                <th className="text-left px-4 py-2 w-1/4">Max</th>
                <th className="text-left px-4 py-2 w-1/4">Fee</th>
                <th className="text-right px-4 py-2 w-1/4"> </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((b) => {
                const id = b.organization_platform_fee_bracket_id;
                const isEditing = editingId === id;
                if (isEditing) {
                  const others = sorted.filter((o) => o.organization_platform_fee_bracket_id !== id);
                  return (
                    <BracketRowForm
                      key={`edit-${id}`}
                      initial={b}
                      otherBrackets={others}
                      disallowUnbounded={disallowUnboundedForRow(b)}
                      onCancel={() => setEditingId(null)}
                      onSubmit={(patch) => updateMutation.mutate({ bracketId: id, patch })}
                      submitting={updateMutation.isPending}
                    />
                  );
                }
                return (
                  <BracketRow
                    key={id}
                    bracket={b}
                    onEdit={() => { setCreating(false); setEditingId(id); }}
                    onDelete={() => {
                      if (!window.confirm(`Delete this bracket (${formatPrice(num(b.min_amount))} – ${isUnbounded(b.max_amount) ? 'unbounded' : formatPrice(num(b.max_amount))} @ ${num(b.fee_percent)}%)?`)) return;
                      setBusyId(id);
                      deleteMutation.mutate(id);
                    }}
                    deleting={busyId === id && deleteMutation.isPending}
                  />
                );
              })}
              {creating && (
                <BracketRowForm
                  key="create"
                  initial={createInitial}
                  otherBrackets={sorted}
                  disallowUnbounded={disallowUnboundedForNew}
                  onCancel={() => setCreating(false)}
                  onSubmit={(payload) => createMutation.mutate(payload)}
                  submitting={createMutation.isPending}
                />
              )}
            </tbody>
          </table>
        </div>
      )}

      {gaps.length > 0 && (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/70 text-[12px] text-amber-900">
          <div className="flex items-start gap-2">
            <RiAlertLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold mb-1">
                Uncovered {gaps.length === 1 ? 'range' : 'ranges'} — falls through
                to <span className="font-mono">organization.platform_fee_percent</span>
                {' '}(or 1.5% default):
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                {gaps.map((g, i) => (
                  <li key={`${g.from}-${g.to}-${i}`}>
                    {formatPrice(g.from)} – {g.to === Infinity ? 'unbounded' : formatPrice(g.to)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesTab;
